import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthenticationContext } from "@/store/authContext/authContext";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SliderRange, SliderThumb, SliderTrack } from "@/components/ui/slider";
import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DECK_TOPICS, makeDefaultDeck } from "@/common/decks";
import type { DeckForCreateModal } from "@/common/decks";
import { todayUTC } from "@/common/dateUtils";
import type { ISODate } from "@/common/dateUtils";
import { Check, LoaderCircle } from "lucide-react";
import SearchReports from "@/components/deck/SearchReports";
import { createDeck } from "@/lib/serverComm";
import EventBus from "@/lib/eventBus";
import cx from "classnames";

const NewDeckModalContent: React.FC = () => {
  const { dispatch } = useAuthenticationContext(),
    navigate = useNavigate(),
    closeModal = () => {
      dispatch({ type: "closeModalAction" }); // Dispatch close action
    };

  const [newDeck, setNewDeck] = useState<DeckForCreateModal>(makeDefaultDeck()),
    [mode, setMode] = useState<"ai" | "manual">("ai"),
    [minutes, setMinutes] = useState<number>(3),
    [lengthMode, setLengthMode] = useState<"minutes" | "slides">("minutes"),
    [loading, setLoading] = useState<boolean>(false);

  const setAiMode = useCallback(() => {
      setNewDeck({ ...newDeck, slides: [] });
      setMode("ai");
    }, [newDeck]),
    setManualMode = useCallback(() => {
      setNewDeck({ ...newDeck, topic: undefined });
      setMode("manual");
      console.log("Manual mode selected");
    }, [newDeck]);

  const sliderText = (() => {
    if (lengthMode === "minutes") {
      return `${minutes} min`;
    } else {
      return `${newDeck.desired_slide_length} slide${
        newDeck.desired_slide_length === 1 ? "" : "s"
      }`;
    }
  })();

  const deckValid = (() => {
    if (loading) return false;
    if (mode === "ai") {
      return (
        newDeck.topic &&
        newDeck.desired_slide_length > 0 &&
        newDeck.desired_timeframe_start <= newDeck.desired_timeframe_end
      );
    } else {
      return newDeck.slides.length > 0;
    }
  })();

  const selectItems = Object.entries(DECK_TOPICS).map(([key, value]) => (
    <SelectItem key={key} value={key}>
      {value.name}
    </SelectItem>
  ));

  const handleSubmitDeck = async () => {
    setLoading(true);
    try {
      const result = await createDeck(newDeck);
      if (result.error) {
        EventBus.$emit("flash:open", "error", result.errorMsg);
      } else {
        EventBus.$emit("flash:open", "success", "Deck created successfully");
        // Navigate to the deck's edit page instead of reloading the decks
        if (result.deck?.id) {
          navigate(`/decks/${result.deck.id}`);
        }
      }
      closeModal();
    } catch (error) {
      console.error("Error submitting deck:", error);
    } finally {
      setLoading(false);
    }
  };

  const aiModeContent = () => (
    <div className="w-full max-w-[420px]">
      <div className="block mb-6">
        <label className="block mb-4 text-base text-deck-primary font-semibold">
          What is your deck about?
        </label>
        <Select
          value={newDeck.topic || ""}
          onValueChange={(value) =>
            setNewDeck({
              ...newDeck,
              topic: value as keyof typeof DECK_TOPICS,
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a topic" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>{selectItems}</SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="block mb-6">
        <label className="block mb-4 text-base text-deck-primary font-semibold">
          How long is your deck? (e.g. 1 slide is about 3 minutes)
        </label>
        <SliderPrimitive.Root
          value={[
            lengthMode === "minutes" ? minutes : newDeck.desired_slide_length,
          ]}
          onValueChange={(value: number[]) => {
            const newLength =
                lengthMode === "minutes" ? Math.round(value[0] / 3) : value[0],
              newMinutes = lengthMode === "minutes" ? value[0] : value[0] * 3;
            setNewDeck({
              ...newDeck,
              desired_slide_length: newLength,
            });
            setMinutes(newMinutes);
          }}
          max={lengthMode === "minutes" ? 45 : 15}
          min={lengthMode === "minutes" ? 3 : 1}
          step={lengthMode === "minutes" ? 3 : 1}
          className="relative flex w-full touch-none select-none items-center"
        >
          <SliderTrack className="bg-deck-primary/30">
            <SliderRange className="bg-deck-primary" />
          </SliderTrack>
          <SliderThumb className="bg-deck-primary border-deck-primary cursor-pointer w-6 h-6" />
        </SliderPrimitive.Root>

        <div className="flex gap-x-6 mt-6">
          <div className="w-28">
            <input
              type="text"
              className="w-full border border-deck-500 rounded-md text-base text-deck-primary text-center"
              value={sliderText}
              readOnly
            />
          </div>
          <div className="flex flex-col gap-y-1">
            <div className="flex items-center gap-x-2">
              <input
                className="shrink-0 border-deck-500 checked:text-deck-primary focus:text-deck-primary focus:ring-0 focus:border-0"
                type="radio"
                id="length-mode-minutes"
                name="length-mode"
                value="minutes"
                checked={lengthMode === "minutes"}
                onChange={() => setLengthMode("minutes")}
              />
              <label
                className="text-deck-primary text-sm"
                htmlFor="length-mode-minutes"
              >
                minutes
              </label>
            </div>
            <div className="flex items-center gap-x-2">
              <input
                className="shrink-0 border-deck-500 checked:text-deck-primary focus:text-deck-primary focus:ring-0 focus:border-0"
                type="radio"
                id="length-mode-slides"
                name="length-mode"
                value="slides"
                checked={lengthMode === "slides"}
                onChange={() => setLengthMode("slides")}
              />
              <label
                className="text-deck-primary text-sm"
                htmlFor="length-mode-slides"
              >
                slides
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="block mb-6">
        <label className="block text-base text-deck-primary font-semibold mb-4">
          Select your data time frame
        </label>
        <div className="flex space-x-4">
          <div className="flex flex-col gap-y-2">
            <label className="text-sm text-deck-primary" htmlFor="start_date">
              Start date
            </label>
            <input
              className="rounded-md border border-deck-500 text-deck-text"
              id="start_date"
              type="date"
              max={todayUTC().toString()}
              value={newDeck.desired_timeframe_start}
              onChange={(e) => {
                if (!e.target.value) return;
                const newStartDate = e.target.value as ISODate;
                const endDate = newDeck.desired_timeframe_end;

                setNewDeck({
                  ...newDeck,
                  desired_timeframe_start: newStartDate,
                  desired_timeframe_end:
                    newStartDate > endDate ? newStartDate : endDate,
                });
              }}
            />
          </div>

          <div className="flex flex-col gap-y-2">
            <label className="text-sm text-deck-primary" htmlFor="end_date">
              End date
            </label>
            <input
              className="rounded-md border border-deck-500 text-deck-text"
              id="end_date"
              type="date"
              max={todayUTC().toString()}
              value={newDeck.desired_timeframe_end}
              onChange={(e) => {
                if (!e.target.value) return;
                const newEndDate = e.target.value as ISODate;
                const startDate = newDeck.desired_timeframe_start;

                setNewDeck({
                  ...newDeck,
                  desired_timeframe_start:
                    newEndDate < startDate ? newEndDate : startDate,
                  desired_timeframe_end: newEndDate,
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const manualModeContent = () => (
    <div className="w-full">
      <SearchReports
        onChange={(reports) =>
          setNewDeck({
            ...newDeck,
            slides: reports.map((report) => report.id),
          })
        }
      />
    </div>
  );

  return (
    <>
      <DialogHeader className="mb-6">
        <DialogTitle className="text-4xl font-bold text-deck-primary mb-4">
          Create a Deck with AI or build it yourself.
        </DialogTitle>
        <DialogDescription className="text-lg text-deck-primary">
          Let AI do the work—automatically pull reports, data, and generate
          speaker notes. Prefer full control? Customize your own deck using
          reports from your database.
        </DialogDescription>
      </DialogHeader>

      <div className="relative inline-flex items-center mb-6 bg-white border border-dek-500 rounded-full p-1 shadow-slide-card max-w-[420px]">
        <input
          className="peer/ai hidden"
          type="radio"
          id="mode-ai"
          name="mode"
          value="ai"
          checked={mode === "ai"}
          onChange={setAiMode}
        />
        <input
          className="peer/custom hidden"
          type="radio"
          id="mode-manual"
          name="mode"
          value="manual"
          checked={mode === "manual"}
          onChange={setManualMode}
        />
        <div className="absolute top-2 left-2 h-[calc(100%-16px)] w-[calc(50%)] rounded-full bg-deck-primary transition-all duration-300 peer-checked/custom:translate-x-[calc(100%-16px)]"></div>
        <label
          htmlFor="mode-ai"
          className="transition-colors z-10 flex-1 cursor-pointer text-center py-3 text-deck-primary peer-checked/ai:font-bold peer-checked/ai:text-white"
        >
          <div className="flex items-center justify-center gap-x-1">
            <Check className="text-white" size={24} />
            <span>Create with AI</span>
          </div>
        </label>
        <label
          htmlFor="mode-manual"
          className="transition-colors z-10 flex-1 cursor-pointer text-center py-3 text-deck-primary peer-checked/custom:font-bold peer-checked/custom:text-white"
        >
          <div className="flex items-center justify-center gap-x-1">
            <Check className="text-white" size={24} />
            <span>Customize My Own</span>
          </div>
        </label>
      </div>

      {mode === "ai" && aiModeContent()}
      {mode === "manual" && manualModeContent()}

      <DialogFooter className="border-t border-deck-500 pt-4 flex justify-between sm:justify-between">
        <button
          className="transition px-4 py-2 bg-white border border-deck-500 text-deck-primary rounded-full font-semibold hover:bg-deck-primary hover:text-white"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          className={cx(
            "transition px-4 py-2 text-white rounded-full font-semibold flex items-center gap-x-2",
            {
              "bg-deck-primary hover:bg-deck-primary/80": deckValid,
              "bg-deck-primary/30 cursor-not-allowed": !deckValid,
            },
          )}
          disabled={!deckValid}
          onClick={handleSubmitDeck}
        >
          <LoaderCircle
            className={loading ? "block animate-spin" : "hidden"}
            size={24}
          />
          Generate slides
        </button>
      </DialogFooter>
    </>
  );
};
export default NewDeckModalContent;
